import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  DeleteOutline as DeleteOutlineIcon,
  Category as CategoryIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import axios from "axios";

const AddForms = ({ onError, onSuccess, currentSession }) => {
  // State management
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showNewCategoryField, setShowNewCategoryField] = useState(false);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/forms/categories`,
        { currentSession },
        { withCredentials: true }
      );

      if (response.data.success) {
        setCategories(response.data.data.categories || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      onError("Error fetching categories");
    }
  }, [currentSession, onError]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    // Enhanced PDF validation
    const invalidFiles = files.filter((file) => {
      const isPdf = file.type === "application/pdf";
      const hasPdfExtension = file.name.toLowerCase().endsWith(".pdf");
      return !isPdf || !hasPdfExtension;
    });

    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map((f) => f.name).join(", ");
      onError(`Only PDF files are allowed. Invalid files: ${invalidNames}`);
      return;
    }

    setSelectedFiles(files);
  };

  // Handle removing individual file from selection
  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  // Handle category creation
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      onError("Category name is required");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/forms/categories/create`,
        {
          name: newCategoryName.trim(),
          currentSession,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        onSuccess("Category created successfully");
        setNewCategoryName("");
        setShowNewCategoryField(false);
        fetchCategories(); // Refresh categories
      } else {
        onError(response.data.message || "Failed to create category");
      }
    } catch (error) {
      console.error("Error creating category:", error);
      onError("Failed to create category");
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      onError("Please select files to upload");
      return;
    }

    if (!selectedCategory) {
      onError("Please select a category");
      return;
    }

    // Double-check PDF validation before upload
    const invalidFiles = selectedFiles.filter((file) => {
      const isPdf = file.type === "application/pdf";
      const hasPdfExtension = file.name.toLowerCase().endsWith(".pdf");
      return !isPdf || !hasPdfExtension;
    });

    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map((f) => f.name).join(", ");
      onError(
        `Invalid files detected: ${invalidNames}. Only PDF files are allowed.`
      );
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append(`files`, file);
      });
      formData.append("categoryId", selectedCategory);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/forms/upload`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress({ percent: percentCompleted });
          },
        }
      );

      if (response.data.success) {
        onSuccess(`Successfully uploaded ${selectedFiles.length} form(s)`);
        setSelectedFiles([]);
        setSelectedCategory("");
        setUploadDialogOpen(false);
      } else {
        onError(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      onError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upload New Forms
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload PDF forms and organize them into categories for easier
          management.
        </Typography>

        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
          size="large"
        >
          Upload Forms
        </Button>
      </Paper>

      {/* Categories Management */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Categories</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setShowNewCategoryField(true)}
            size="small"
          >
            Add Category
          </Button>
        </Box>

        {showNewCategoryField && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="New Category Name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleCreateCategory}
                    disabled={loading || !newCategoryName.trim()}
                    size="small"
                  >
                    {loading ? <CircularProgress size={16} /> : "Create"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowNewCategoryField(false);
                      setNewCategoryName("");
                    }}
                    size="small"
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {categories.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No categories created yet. Create your first category to organize
              forms.
            </Typography>
          ) : (
            categories.map((category) => (
              <Chip
                key={category.id}
                label={category.name}
                icon={<CategoryIcon />}
                color="primary"
                variant="outlined"
              />
            ))
          )}
        </Box>
      </Paper>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Forms</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select PDF files to upload and assign them to a category for
              better organization.
            </Typography>

            {/* Category Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Category *</InputLabel>
              <Select
                value={selectedCategory}
                label="Category *"
                onChange={(e) => setSelectedCategory(e.target.value)}
                startAdornment={
                  <CategoryIcon sx={{ mr: 1, color: "text.secondary" }} />
                }
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* File Selection */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
                size="large"
              >
                Select PDF Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf"
                  onChange={handleFileSelect}
                />
              </Button>
            </Box>

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Selected Files ({selectedFiles.length}):
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                  {selectedFiles.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                        p: 0.5,
                        borderRadius: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Typography variant="body2">
                        • {file.name} ({formatFileSize(file.size)})
                      </Typography>
                      <Tooltip title="Remove file">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile(index)}
                          color="error"
                          sx={{ ml: 1 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Upload Progress */}
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Uploading... {uploadProgress.percent}%
                </Typography>
                <Box
                  sx={{ width: "100%", bgcolor: "grey.200", borderRadius: 1 }}
                >
                  <Box
                    sx={{
                      width: `${uploadProgress.percent || 0}%`,
                      height: 8,
                      bgcolor: "primary.main",
                      borderRadius: 1,
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialogOpen(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={
              selectedFiles.length === 0 || !selectedCategory || uploading
            }
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddForms;
